/**
 * The words for a wrong value (pedagogy spec §5.3.6): the authored diagnosis when the item has
 * one, else the near-miss kind, else nothing (the caller decides on a generic line). It serves
 * both states of an answer:
 *
 * - **pending**, where the response carries the kind only (`{ kind }`), so the text is a process
 *   cue that names no direction, no exponent and no unit (spec §5.3.4);
 * - **terminal**, where the full near miss says which way the answer is off.
 *
 * The two are told apart by what the near miss carries (`from`, `typed`, `factor`), so one
 * function words both and a pending response can never print a terminal text.
 */
import { Localized } from "@client/components/Localized";
import { useT, type TFunction } from "@client/i18n";
import { formatUnit } from "@client/lib/units";
import type { NearMissKind, PartDiagnosis } from "./types";

/** A full near miss (`NearMiss`) or the pending `{ kind }`; the extra fields are optional. */
export interface NearMissLike {
  kind: NearMissKind;
  from?: string;
  to?: string;
  typed?: string;
  expected?: string;
  factor?: 2 | 0.5 | "2pi" | "1/2pi";
}

function unitLabel(t: TFunction, unit: string): string {
  return formatUnit(unit) || t("numeric.dimensionless");
}

/** One sentence for a near miss; `unit` is the part's own unit (a pending cue names it). */
export function nearMissText(t: TFunction, nearMiss: NearMissLike, unit: string): string {
  switch (nearMiss.kind) {
    case "power-of-ten":
      return t("practice.nearMiss.powerOfTen");
    case "unit":
      return nearMiss.from !== undefined && nearMiss.to !== undefined
        ? t("practice.nearMiss.unit", {
            from: formatUnit(nearMiss.from),
            to: formatUnit(nearMiss.to),
          })
        : t("practice.nearMiss.pending.unit", { unit: unitLabel(t, unit) });
    case "unit-mismatch":
      return nearMiss.typed !== undefined && nearMiss.expected !== undefined
        ? t("practice.nearMiss.unitMismatch", {
            typed: formatUnit(nearMiss.typed),
            expected: unitLabel(t, nearMiss.expected),
          })
        : t("practice.nearMiss.pending.unitMismatch", { unit: unitLabel(t, unit) });
    case "sign":
      return t("practice.nearMiss.sign");
    case "factor":
      if (nearMiss.factor === undefined) return t("practice.nearMiss.pending.factor");
      if (nearMiss.factor === 2) return t("practice.nearMiss.factorTwo");
      if (nearMiss.factor === 0.5) return t("practice.nearMiss.factorHalf");
      return t("practice.nearMiss.factorTwoPi");
  }
}

/** A pending cue for `sign` is the only kind-only text that differs from the terminal one. */
function pendingText(t: TFunction, nearMiss: NearMissLike, unit: string): string {
  return nearMiss.kind === "sign"
    ? t("practice.nearMiss.pending.sign")
    : nearMissText(t, nearMiss, unit);
}

export interface DiagnosisMessageProps {
  /** The authored diagnosis; it wins over a near miss (the server sends only one of them). */
  diagnosis?: PartDiagnosis | undefined;
  nearMiss?: NearMissLike | undefined;
  /** The part's unit, named by the pending unit cues. */
  unit: string;
  /** A withheld try: the kind only, worded as a cue without a direction. */
  pending?: boolean;
  className?: string;
}

/** Renders nothing when there is neither a diagnosis nor a near miss. */
export function DiagnosisMessage({
  diagnosis,
  nearMiss,
  unit,
  pending = false,
  className,
}: DiagnosisMessageProps) {
  const t = useT();
  if (diagnosis) {
    return (
      <div className={className}>
        <Localized value={diagnosis.message} variant="inline" />
      </div>
    );
  }
  if (!nearMiss) return null;
  return (
    <span className={className}>
      {pending ? pendingText(t, nearMiss, unit) : nearMissText(t, nearMiss, unit)}
    </span>
  );
}

/** True when a pending part carries something specific to say (so no generic line is needed). */
export function hasCue(part: { nearMiss?: unknown; diagnosis?: unknown }): boolean {
  return part.nearMiss !== undefined || part.diagnosis !== undefined;
}
